import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useMutation } from '@apollo/client'

import { useCallback, useState } from 'react'

export const useCcAppPromoUserDataFragment = graphql(`
  fragment UseCcAppPromo_UserDataFragment on User {
    id
    hasPlacedCcOrderViaMobile
    nativeAppSettings {
      hasApp
    }
    onboardingSettings {
      ccAppPromoDismissed
    }
    account {
      id
    }
    tenant {
      id
      mobileApp {
        canteenAppStoreUrl
        canteenPlayStoreUrl
      }
    }
  }
`)

const dismissCcAppPromoMutation = graphql(`
  mutation UseCcAppPromo_DismissMutation {
    updateMe(userPayload: { onboardingSettings: { ccAppPromoDismissed: true } }) {
      id
    }
  }
`)

// At least one URL is set; a missing store just hides its toggle option
export type CcAppPromo = {
  iosUrl: string | null
  androidUrl: string | null
  dismiss: () => void
}

export const useCcAppPromo = (
  userDataProp: FragmentType<typeof useCcAppPromoUserDataFragment> | undefined,
): CcAppPromo | null => {
  const userData = useFragment(useCcAppPromoUserDataFragment, userDataProp)
  const enabled = useFeatureFlag(SPLITS.CC_APP_PROMO, {
    accountId: userData?.account?.id ?? '',
    userId: userData?.id ?? '',
  })
  // Hides the card for this page load; the server value takes over on the next one
  const [dismissed, setDismissed] = useState(false)
  const [runDismiss] = useMutation(dismissCcAppPromoMutation, {
    onError: () => setDismissed(false),
  })

  const dismiss = useCallback(() => {
    setDismissed(true)
    runDismiss()
  }, [runDismiss])

  if (!enabled || !userData || dismissed) return null
  if (userData.onboardingSettings.ccAppPromoDismissed) return null
  if (userData.hasPlacedCcOrderViaMobile) return null
  if (userData.nativeAppSettings.hasApp) return null

  const iosUrl = userData.tenant.mobileApp?.canteenAppStoreUrl || null
  const androidUrl = userData.tenant.mobileApp?.canteenPlayStoreUrl || null
  if (!iosUrl && !androidUrl) return null

  return { iosUrl, androidUrl, dismiss }
}
